import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon, BanknotesIcon, CheckIcon,
} from '@heroicons/react/24/outline';
import api from '../../api/client';

// Director-only settings page for the points wallet. Every number
// on the wallet system is editable here — no hardcoded magic in
// walletController. Grouped for readability, saved together.

const GROUPS = [
  {
    title: 'Cashout',
    items: [
      { key: 'cashout_min_points',
        label: 'Minimum balance to cash out',
        hint: 'Staff can cash out when their balance is at or above this many points.',
        unit: 'pts' },
      { key: 'cashout_cash_cents',
        label: 'Cashout payout (in cents)',
        hint: 'How much cash the staff receives per cashout — currently 5000 = $50 for 100 points.',
        unit: '¢' },
    ],
  },
  {
    title: 'Deposits',
    items: [
      { key: 'hire_bonus',
        label: 'Hire bonus',
        hint: 'Automatically added to new hires on move-forward.',
        unit: 'pts' },
    ],
  },
  {
    title: 'Withdrawals (infraction penalties)',
    items: [
      { key: 'late_arrival',
        label: 'Late arrival (>5 min)',
        hint: 'Value should be negative (e.g. -5).',
        unit: 'pts' },
      { key: 'call_off',
        label: 'Call off',
        hint: 'Value should be negative.',
        unit: 'pts' },
      { key: 'call_off_doctor',
        label: 'Call off with doctor note',
        hint: 'Value should be negative (less severe than a regular call off).',
        unit: 'pts' },
      { key: 'late_break',
        label: 'Late back from break (>5 min)',
        hint: 'Value should be negative.',
        unit: 'pts' },
    ],
  },
  {
    title: 'Thresholds + toggles',
    items: [
      { key: 'probation_threshold',
        label: 'Probation threshold',
        hint: 'Below this balance, staff shows on the attention roster.',
        unit: 'pts' },
      { key: 'doctor_note_deadline_hours',
        label: 'Doctor\'s note submission deadline',
        hint: 'How many hours after their shift starts staff have to upload a doctor\'s note. Past this, submissions from staff are blocked (director + admin can still submit on their behalf).',
        unit: 'hrs' },
    ],
    toggles: [
      { key: 'perfect_month_enabled',
        label: 'Monthly perfect-attendance bonus',
        hint: 'When on, staff with zero infractions in a month earn a bonus equal to hours worked (nets $0.50/hr).' },
    ],
  },
];

export default function WalletSettingsPage() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/wallet/config')
      .then(({ data }) => setConfig(data))
      .catch(() => toast.error('Could not load wallet config.'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch('/wallet/config', config);
      setConfig(data);
      toast.success('Wallet settings saved.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed.');
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  }
  if (!config) return null;

  return (
    <div className="animate-slide-up max-w-3xl mx-auto">
      <Link to="/settings" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeftIcon className="w-4 h-4" /> Back to Settings
      </Link>
      <div className="mb-6 flex items-start gap-4">
        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
          <BanknotesIcon className="w-6 h-6 text-orange-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Points Wallet Settings</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Tune the numbers. Every change applies immediately to future events; existing
            wallet history stays as-is.
          </p>
        </div>
      </div>

      <div className="space-y-5">
        {GROUPS.map(g => (
          <div key={g.title} className="bg-white rounded-2xl border border-gray-200 p-5">
            <h2 className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-3">{g.title}</h2>
            <div className="space-y-3">
              {(g.items || []).map(it => (
                <div key={it.key} className="grid grid-cols-1 md:grid-cols-[1fr_140px] gap-3 md:items-center">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{it.label}</p>
                    {it.hint && <p className="text-xs text-gray-500 mt-0.5">{it.hint}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="number"
                      value={config[it.key] ?? ''}
                      onChange={(e) => setConfig({ ...config, [it.key]: e.target.value === '' ? '' : Number(e.target.value) })}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-right" />
                    <span className="text-xs text-gray-500 w-6">{it.unit}</span>
                  </div>
                </div>
              ))}
              {(g.toggles || []).map(t => (
                <label key={t.key} className="flex items-start gap-3 cursor-pointer pt-2">
                  <input type="checkbox"
                    checked={!!config[t.key]}
                    onChange={(e) => setConfig({ ...config, [t.key]: e.target.checked })}
                    className="mt-1" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t.label}</p>
                    {t.hint && <p className="text-xs text-gray-500 mt-0.5">{t.hint}</p>}
                  </div>
                </label>
              ))}
            </div>
          </div>
        ))}

        <div className="flex justify-end gap-2 sticky bottom-4 bg-white/80 backdrop-blur rounded-2xl border border-gray-200 p-3 shadow-sm">
          <button onClick={save} disabled={saving}
            className="text-sm px-5 py-2 rounded-lg bg-orange-500 text-white font-semibold hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-1">
            <CheckIcon className="w-4 h-4" /> {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-500 mt-4">
        Monthly perfect-attendance calculation runs at end-of-month once Phase 2 ships.
        Manual adjustments are available under each staff member's profile.
      </p>
    </div>
  );
}
